"use client";

import React, { useEffect, useRef, useState } from "react";
import { Bed, ShieldCheck, Utensils, Users, House, Coffee, Leaf } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const features = [{
  title: "Private Rooms in a Small Assisted Living Home",
  description: "Comfortable, fully furnished private rooms in our small assisted living home in Silver Spring, designed to provide rest, privacy, and dignity for every resident.",
  icon: Bed
}, {
  title: "Personalized Care in Our Residential Assisted Living",
  description: "Our compassionate caregivers provide personalized support 24/7, ensuring each resident receives the attention and care they deserve in our intimate Silver Spring home.",
  icon: ShieldCheck
}, {
  title: "Home-Cooked Family Meals",
  description: "Nutritious, home-cooked meals are prepared daily in our kitchen and tailored to each resident’s dietary needs and preferences.",
  icon: Utensils
}, {
  title: "Small Assisted Living Home Atmosphere",
  description: "Our small home setting allows us to provide highly personalized attention and a warm, family-like environment where every resident is truly known and cared for.",
  icon: Users
}, {
  title: "Safe & Secure Assisted Living in Maryland",
  description: "Our residential assisted living home in Silver Spring provides a safe and secure environment with thoughtful safety features and attentive caregiver support.",
  icon: House
}, {
  title: "Community & Companionship",
  description: "Residents enjoy meaningful daily interaction, companionship, and engaging activities within our close-knit home community.",
  icon: Coffee
}];
function FeatureCard({
  feature,
  index
}: {
  feature: typeof features[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 100}ms`
  }}>
      <Card className="h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-border/40 bg-card/60 backdrop-blur-sm group">
        <CardHeader className="pb-4">
          <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center mb-4 transition-colors duration-300 group-hover:bg-secondary/20">
            <feature.icon className="w-7 h-7 text-secondary" strokeWidth={1.5} />
          </div>
          <CardTitle className="text-xl font-serif text-foreground tracking-tight">
            {feature.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-base text-muted-foreground leading-relaxed">
            {feature.description}
          </CardDescription>
        </CardContent>
      </Card>
    </div>;
}
export default function TheComfortsOfHome() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="the-comforts-of-home" className="relative bg-background py-24 md:py-32 overflow-hidden">
      {}
      <Leaf className="absolute -bottom-32 -right-32 w-[40rem] h-[40rem] text-primary/[0.03] -rotate-12 pointer-events-none" strokeWidth={0.5} aria-hidden="true" />
      <Leaf className="absolute -top-40 -left-40 w-[30rem] h-[30rem] text-secondary/[0.03] rotate-45 pointer-events-none" strokeWidth={0.5} aria-hidden="true" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`max-w-4xl mx-auto text-center mb-16 md:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-foreground mb-6 tracking-tight">
            Family First Senior Care – Assisted Living in Silver Spring, Maryland
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">Experience a boutique residential assisted living home in Montgomery County, offering a small assisted living home with personalized, family-like care for up to 4 or 5 residents.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => <FeatureCard key={feature.title} feature={feature} index={index} />)}
        </div>
      </div>
    </section>;
}